import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import styles from "./MGResponsiveSheet.module.css";

export type TMGResponsiveSheetProps = {
  open: boolean;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  dataTestId?: string;
  sheetClassName?: string;
  sheetStyle?: React.CSSProperties;
  titleClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  desktopMaxWidth?: number | string;
  closeOnOverlayClick?: boolean;
  dismissible?: boolean;
  onClose: () => void;
};

export const MGResponsiveSheet = ({
  open,
  title,
  children,
  footer,
  dataTestId,
  sheetClassName,
  sheetStyle,
  titleClassName,
  bodyClassName,
  footerClassName,
  desktopMaxWidth = 856,
  closeOnOverlayClick = true,
  dismissible = true,
  onClose,
}: TMGResponsiveSheetProps) => {
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartYRef = useRef(0);
  const dragOffsetRef = useRef(0);
  const titleId = dataTestId ? `${dataTestId}-title` : "responsive-sheet-title";
  const normalizedDesktopMaxWidth =
    typeof desktopMaxWidth === "number" ? `${desktopMaxWidth}px` : desktopMaxWidth;

  const resetDrag = () => {
    dragOffsetRef.current = 0;
    setDragOffset(0);
    setIsDragging(false);
  };

  const handleClose = () => {
    if (!dismissible) return;
    resetDrag();
    onClose();
  };

  const handleOverlayClick = () => {
    if (dismissible && closeOnOverlayClick) handleClose();
  };

  const handleDragStart = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dismissible) return;
    if (window.matchMedia("(min-width: 640px)").matches) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;

    dragStartYRef.current = event.clientY;
    dragOffsetRef.current = 0;
    setDragOffset(0);
    setIsDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleDragMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;

    const nextOffset = Math.max(0, event.clientY - dragStartYRef.current);
    dragOffsetRef.current = nextOffset;
    setDragOffset(nextOffset);
    event.preventDefault();
  };

  const handleDragEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    if (dragOffsetRef.current > 120) {
      handleClose();
      return;
    }

    resetDrag();
  };

  if (!open) return null;

  return createPortal(
    <div
      className={styles.overlay}
      data-testid={dataTestId && `${dataTestId}-overlay`}
      onClick={handleOverlayClick}>
      <div
        className={styles.sheetContainer}
        data-testid={dataTestId && `${dataTestId}-container`}
        style={
          {
            "--mg-responsive-sheet-desktop-max-width": normalizedDesktopMaxWidth,
          } as React.CSSProperties
        }>
        <div
          className={`${styles.sheet} ${isDragging ? styles.dragging : ""} ${sheetClassName || ""}`}
          style={{ ...sheetStyle, transform: `translateY(${dragOffset}px)` }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          data-testid={dataTestId}
          onClick={(event) => event.stopPropagation()}>
          <div
            className={styles.dragArea}
            data-testid={dataTestId && `${dataTestId}-drag-area`}
            onPointerDown={handleDragStart}
            onPointerMove={handleDragMove}
            onPointerUp={handleDragEnd}
            onPointerCancel={handleDragEnd}>
            <div className={styles.handle} />
          </div>

          <div className={styles.header}>
            <h2 className={`${styles.title} ${titleClassName || ""}`} id={titleId}>
              {title}
            </h2>
          </div>

          <div className={`${styles.body} ${bodyClassName || ""}`}>{children}</div>

          {footer && <div className={`${styles.footer} ${footerClassName || ""}`}>{footer}</div>}
        </div>

        {dismissible && (
          <button
            className={`${styles.closeButton} ${styles.closeButtonOutside}`}
            type="button"
            aria-label="Fechar"
            data-testid={dataTestId && `${dataTestId}-close-button`}
            onClick={(event) => {
              event.stopPropagation();
              handleClose();
            }}>
            <X size={32} />
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
};
